/**
 * DeskBoardView — the Desk's Board view (FEAT-087 / S-366).
 *
 * The same pieces as the Queue, laid out by where they sit rather than by
 * what they need from you. It renders inside the Desk shell, so it has no
 * `<main>` and no selector of its own — both belong to DeskView.
 *
 * Card kind comes from `queueGroupFor`, the same function the Queue groups
 * by. That is deliberate: the two views cannot disagree about what is
 * waiting on a person, because they ask the same question of the same
 * field. Neutral cards (running, scheduled, published) get the grey wash
 * instead of a kind colour, so quiet states stay quiet.
 *
 * **CHANGE**, per the handoff: idea cards carry the same solid pill and the
 * same "Start the pipeline" action as the Queue's Start group. The shipped
 * board parked them with no affordance at all; an idea is waiting on a
 * person, and the board should say so.
 *
 * Values are the design file's own markup. Two places where it disagrees
 * with the README's prose summary, comp winning per the handoff's rule:
 * the column rule is 3px (README says 2px), and cards are 10px 12px at
 * 10px radius.
 */

import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiQuery } from "@/lib/api";
import { LoadingState } from "@/components/LoadingState";
import { Icon } from "@/components/Icon";
import { useWorkspaceContext } from "@/lib/workspace-context";
import { useDateFormat } from "@/lib/useDateFormat";
import { formatDateTime } from "@/lib/date-format";
import { describeRunError } from "@/lib/run-errors";
import {
  KANBAN_COLUMNS,
  kanbanColumnForPiece,
  pieceStateLabel,
  type KanbanColumn,
} from "@/lib/ui-state";
import {
  isOverdue,
  queueGroupFor,
  stoppedByAgent,
  type QueueGroup,
} from "@/lib/queue-groups";
import {
  BOARD_WINDOWS,
  DEFAULT_BOARD_WINDOW,
  filterBoardPieces,
  parseBoardWindow,
  serialiseBoardWindow,
  type BoardWindow,
} from "@/lib/board-window";
import { useDeskContext } from "./DeskView";
import { useRole } from "@/lib/useRole";

interface PieceRow {
  id: number;
  title: string;
  slug: string;
  content_type: string;
  ui_state: string;
  generation_phases: string | null;
  run_phase: string | null;
  publication_date: string | null;
  updated_at: string;
  deleted_at: string | null;
}

interface PiecesListResponse {
  data: PieceRow[];
  meta: { total: number; limit: number; offset: number };
}

interface AgencyclientLite {
  id: number;
  runMode?: "auto" | "manual";
  run_mode?: "auto" | "manual";
}

/** The five stage-ramp rules under the column headers, in column order. */
const COLUMN_BAR: Record<string, string> = {
  ideas: "var(--stage-bar-1)",
  ai_working: "var(--stage-bar-2)",
  awaiting_human: "var(--stage-bar-3)",
  scheduled: "var(--stage-bar-4)",
  published: "var(--stage-bar-5)",
};

const BOARD_WINDOW_KEY = "ccm:board_window";
const BOARD_ACTIVE_KEY = "ccm:board_all_active";

function readStoredWindow(): BoardWindow {
  if (typeof globalThis.window === "undefined") return DEFAULT_BOARD_WINDOW;
  try {
    return parseBoardWindow(localStorage.getItem(BOARD_WINDOW_KEY));
  } catch {
    return DEFAULT_BOARD_WINDOW;
  }
}

/** Defaults on: the founder's "7 days + in process". */
function readStoredAllActive(): boolean {
  if (typeof globalThis.window === "undefined") return true;
  try {
    return localStorage.getItem(BOARD_ACTIVE_KEY) !== "0";
  } catch {
    return true;
  }
}

export function DeskBoardView() {
  const { openCreatePiece } = useDeskContext();
  const { canEdit } = useRole();
  const navigate = useNavigate();
  const dateFormat = useDateFormat();
  const { activeWorkspace, allWorkspaces, isLoading: wsLoading } = useWorkspaceContext();
  const activeId = activeWorkspace?.id;

  const { data, isLoading, error } = useApiQuery<PiecesListResponse>(
    ["pieces", activeId ?? 0, "desk-board"],
    `/pieces?agencyclient_id=${activeId}`,
    { refetchOnWindowFocus: true, enabled: !!activeId && !allWorkspaces }
  );

  // FEAT-009 / S-107 — the run endpoint refuses with 409 run_mode_manual in
  // manual workspaces, so the Start action is hidden there rather than
  // offered and then failing. Same shared "/agencyclients" cache key
  // PieceView uses, so this is not an extra round trip in practice.
  const agencyclientsQ = useApiQuery<AgencyclientLite[]>(["agencyclients"], "/agencyclients");
  const runMode =
    agencyclientsQ.data?.find((a) => a.id === activeId)?.runMode ??
    agencyclientsQ.data?.find((a) => a.id === activeId)?.run_mode ??
    "auto";
  const isManualMode = runMode === "manual";

  // The board accumulates every piece ever published, so the useful work
  // sinks below months of finished work. The window trims by recency; work
  // still in flight ignores it, because a piece stuck for weeks is the one
  // most worth seeing and a plain date filter would hide exactly that.
  const [window, setWindowState] = useState<BoardWindow>(readStoredWindow);
  const [allActive, setAllActive] = useState<boolean>(readStoredAllActive);
  const chooseWindow = useCallback((next: BoardWindow) => {
    setWindowState(next);
    try {
      localStorage.setItem(BOARD_WINDOW_KEY, serialiseBoardWindow(next));
    } catch {
      // Unavailable storage just means the choice is not remembered.
    }
  }, []);
  const toggleAllActive = useCallback(() => {
    setAllActive((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(BOARD_ACTIVE_KEY, next ? "1" : "0");
      } catch {
        // as above
      }
      return next;
    });
  }, []);

  const { columns, hidden } = useMemo(() => {
    const all = (data?.data ?? []).filter((p) => !p.deleted_at);
    const { visible, hidden } = filterBoardPieces(all, {
      window,
      alwaysShowActive: allActive,
      // "Active" is anything that has not reached Published: the board's own
      // last column is exactly the finished pile the window is here to trim.
      isActive: (p) => kanbanColumnForPiece(p) !== "published",
      // The date the piece went out, not the last time its row was touched.
      // updated_at moves for reasons that say nothing about how current a
      // finished piece is — an edit, a re-tag, a backfill. One backfill over
      // 161 rows put every piece published since March inside a 7-day window
      // and the filter looked broken, because for that column it was.
      // Falls back to updated_at only when there is no publication date.
      timestampOf: (p) => p.publication_date ?? p.updated_at,
    });
    const byColumn = new Map<KanbanColumn, PieceRow[]>();
    for (const piece of visible) {
      const col = kanbanColumnForPiece(piece);
      if (!col) continue;
      const bucket = byColumn.get(col);
      if (bucket) bucket.push(piece);
      else byColumn.set(col, [piece]);
    }
    return {
      columns: KANBAN_COLUMNS.map((def) => ({
        def,
        pieces: byColumn.get(def.id as KanbanColumn) ?? [],
      })),
      hidden,
    };
  }, [data, window, allActive]);

  if (wsLoading || isLoading) return <LoadingState label="Loading the board…" />;

  if (error) {
    return (
      <div className="state-error">
        {error instanceof ApiError && error.status === 403
          ? "You don't have permission to view this workspace."
          : (error.message ?? "Could not load the board.")}
      </div>
    );
  }

  return (
    <div className="desk-board">
      <header className="desk-head cal-head">
        <div>
          <h1 className="desk-title">The whole current</h1>
          <p className="desk-sub">
            Same pieces, laid out by where they sit. Actions live in the queue.
          </p>
          {/* What the window removed, under the subtitle it qualifies —
              rather than floating at the far right where it read as unrelated
              chrome. */}
          <p className="desk-sub board-filter-note">
            {hidden === 0
              ? "Showing everything in this workspace"
              : `${hidden} older ${hidden === 1 ? "piece" : "pieces"} hidden by the window`}
          </p>
        </div>
        <div className="cal-head-r">
          <div className="cal-modes" role="tablist" aria-label="How far back">
            {BOARD_WINDOWS.map((w) => (
              <button
                key={String(w.value)}
                role="tab"
                aria-selected={window === w.value}
                className={`cal-mode ${window === w.value ? "is-active" : ""}`}
                onClick={() => chooseWindow(w.value)}
              >
                {w.label}
              </button>
            ))}
          </div>
          <label className="board-filter-toggle">
            <input type="checkbox" checked={allActive} onChange={toggleAllActive} />
            <span>Always show work in progress</span>
          </label>
          {canEdit && (
            <button type="button" className="desk-new" onClick={openCreatePiece}>
              + New piece
            </button>
          )}
        </div>
      </header>

      <div className="board-cols">
        {columns.map(({ def, pieces }) => (
          <section key={def.id} className="board-col">
            <div className="board-col-head">
              <span className="board-col-title">{def.title}</span>
              <span className="board-col-count">{pieces.length}</span>
            </div>
            <div
              className="board-col-rule"
              style={{ background: COLUMN_BAR[def.id] ?? "var(--rule)" }}
            />
            <div className="board-cards">
              {pieces.map((piece) => (
                <BoardCard
                  key={piece.id}
                  piece={piece}
                  dateFormat={dateFormat}
                  isManualMode={isManualMode}
                  onOpen={() => navigate(`/piece/${piece.id}`)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

interface BoardCardProps {
  piece: PieceRow;
  dateFormat: ReturnType<typeof useDateFormat>;
  isManualMode: boolean;
  onOpen: () => void;
}

function BoardCard({ piece, dateFormat, isManualMode, onOpen }: BoardCardProps) {
  const api = useApi();
  const qc = useQueryClient();
  const [runError, setRunError] = useState<string | null>(null);

  const kind: QueueGroup | null = queueGroupFor(piece);
  const overdue = isOverdue(piece);

  // Agent in the label text, kind on the colour — the card says who
  // stopped it, the pill colour says what it wants. Cards that are not
  // waiting on anyone keep the app's existing state vocabulary.
  const status = kind ? stoppedByAgent(piece) : pieceStateLabel(piece);

  const startRun = useMutation({
    mutationFn: async () => api(`/pieces/${piece.id}/run`, { method: "POST" }),
    onSuccess: () => {
      setRunError(null);
      void qc.invalidateQueries({ queryKey: ["pieces"] });
    },
    onError: (e) => setRunError(describeRunError(e)),
  });

  // CHANGE — an idea is waiting on a person, so the board offers the same
  // start affordance the queue does instead of parking it silently.
  // FEAT-108 / S-438 — starting a run is Admin and Editor work.
  const { canEdit } = useRole();
  const canStart = kind === "start" && !isManualMode && canEdit;

  return (
    <div
      className={`board-card ${overdue ? "is-overdue" : ""}`}
      data-kind={kind ?? "neutral"}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="board-status">
        {kind && <span className="board-status-dot" />}
        {status}
      </span>
      <span className="board-card-title">{piece.title || piece.slug}</span>
      <div className="board-card-meta">
        <span>#{piece.id}</span>
        <span className={overdue ? "board-card-late" : "board-card-when"}>
          {formatDateTime(piece.updated_at, dateFormat, { embedded: true })}
        </span>
      </div>

      {canStart && (
        <div className="board-card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="board-act board-act-start"
            title="Start the pipeline"
            aria-label="Start the pipeline"
            disabled={startRun.isPending}
            onClick={() => startRun.mutate()}
          >
            <Icon name="chevron-r" size={14} stroke={1.8} />
          </button>
        </div>
      )}
      {/* House rule from the handoff: wherever a control is disabled or
          absent, the UI says why in adjacent text. */}
      {kind === "start" && isManualMode && (
        <span className="board-card-note">Manual workspace — start it from the piece.</span>
      )}
      {runError && <span className="board-card-error">{runError}</span>}
    </div>
  );
}
